import { useCallback, useMemo } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import classNames from 'classnames';

import Loader from 'src/components/common/loader';

import { MAP_DEFAULT_ZOOM, MAP_LOT_DEFAULT_ZOOM, MAP_MAX_ZOOM_OUT } from 'src/constants';
import { useGoogleMaps } from 'src/contexts/google-maps-context';
import type { MapProps } from './map.props';

import './map.scss';

const mapOptions = {
  disableDefaultUI: true,
  fullscreenControl: false,
  mapTypeControl: false,
  mapTypeId: 'satellite',
  maxZoom: MAP_LOT_DEFAULT_ZOOM,
  minZoom: MAP_MAX_ZOOM_OUT,
  rotateControl: false,
  streetViewControl: false,
  zoomControl: true,
};

function createControl(
  className: string,
  ariaLabel: string,
  handleClick: () => void
): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = className;
  button.setAttribute('aria-label', ariaLabel);
  button.title = ariaLabel;
  button.addEventListener('click', handleClick);

  return button;
}

const Map = ({
  children,
  className,
  center,
  height,
  isFullScreen,
  width,
  zoom = MAP_DEFAULT_ZOOM,
  handleFullScreenButtonClick,
  handleHomeButtonClick,
  onClick,
  onLoad,
  onZoomChanged,
}: MapProps) => {
  const { isLoaded } = useGoogleMaps();

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      if (map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].getLength() === 0) {
        const controlsWrapper = document.createElement('div');
        controlsWrapper.className = 'map__controls-wrapper';

        const homeButton = createControl(
          'map__control map__control_home',
          'Navigate to initial location',
          handleHomeButtonClick
        );

        const fullScreenButton =
          typeof handleFullScreenButtonClick === 'function'
            ? createControl(
                classNames(
                  'map__control',
                  isFullScreen ? 'map__control_fullscreen_popup' : 'map__control_fullscreen'
                ),
                'Toggle fullscreen',
                handleFullScreenButtonClick
              )
            : undefined;

        controlsWrapper.append(homeButton);
        if (fullScreenButton) {
          controlsWrapper.append(fullScreenButton);
        }
        controlsWrapper.addEventListener(
          'contextmenu',
          (e: MouseEvent) => {
            e.preventDefault();
          },
          true
        );

        map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controlsWrapper);
      }

      onLoad(map);
    },
    [handleFullScreenButtonClick, handleHomeButtonClick, isFullScreen, onLoad]
  );

  const containerStyle = useMemo(() => ({ height, width }), [height, width]);

  return (
    <section
      className={classNames('map', className)}
      aria-label="Google Maps"
      role="application"
      style={containerStyle}
    >
      {isLoaded ? (
        <GoogleMap
          center={center}
          mapContainerStyle={containerStyle}
          options={mapOptions}
          zoom={zoom}
          onClick={onClick}
          onLoad={handleMapLoad}
          onZoomChanged={onZoomChanged}
        >
          {children}
        </GoogleMap>
      ) : (
        <Loader isDebounce={false} />
      )}
    </section>
  );
};

export default Map;
